import css from '@emotion/css'
import { DataTable, HFlow, Icon, TableFooter, Text, VFlow } from 'bold-ui'
import { Ellipsis } from 'components/Ellipsis'
import { DEFAULT_SELECT_PAGE_PARAM } from 'components/form/field/select/useAsyncQuerySelect'
import { TableBox } from 'components/table'
import { useListaRegulacaoQuery } from 'graphql/hooks.generated'
import { StatusRegulacaoEnum, TipoRegulacaoEnum } from 'graphql/types.generated'
import React from 'react'
import { isUndefinedOrNull } from 'util/checks'
import { formatDate } from 'util/date/formatDate'
import { humanizeDateTime } from 'util/date/humanize-date'
import StatusSquare from 'view/atendimentos/detail/historico/components/StatusSquare'
import { cuidadoCompartilhadoClassificacaoPrioridadeRecord } from 'view/atendimentos/detail/soap/plano/cuidado-compartilhado/model-cuidadocompartilhado'

import {
  fakeDataExame,
  fakeDataGuiaEncaminhamento,
  ListaRegulacamTableItem,
  StatusRegulacaoEnumMap,
} from '../model-regulacao'
import { ListaRegulcaoTableAction } from './ListaRegulacaoTableAction'

export const ListaRegulcaoTable = () => {
  const { data, loading } = useListaRegulacaoQuery({
    variables: { input: { filtro: null, equipeId: null, unidadeSaudeId: null, pageParams: DEFAULT_SELECT_PAGE_PARAM } },
  })

  return (
    <TableBox>
      <DataTable<ListaRegulacamTableItem>
        loading={loading}
        rows={[fakeDataGuiaEncaminhamento, fakeDataExame, ...(data.listaRegulacao?.content || [])]}
        columns={[
          {
            name: 'ultimaAtualizacao',
            header: 'Ultima Atualização',
            render: renderAtualizacao,
            style: styles.columnWidth('16%'),
          },
          {
            name: 'solicitacao',
            header: 'Solicitação',
            render: renderSolicitacao,
            style: styles.columnWidth('28%'),
          },
          {
            name: 'cidadao',
            header: 'Cidadão',
            render: renderCidadao,
            style: styles.columnWidth('24%'),
          },
          {
            name: 'solicitante',
            header: 'Solicitante',
            render: renderSolicitante,
          },
          {
            name: 'visualizar',
            header: '',
            render: ListaRegulcaoTableAction,
          },
        ]}
      />
      <TableFooter {...data.listaRegulacao?.pageInfo} sizeOptions={[5, 10, 30, 50]} style={styles.tableFooter} />
    </TableBox>
  )
}

const renderStatus = (status: StatusRegulacaoEnum) => {
  const { icon: icone, color, text } = StatusRegulacaoEnumMap[status]
  return (
    <HFlow hSpacing={0.25} alignItems='center'>
      <Icon icon={icone} color={color} size={1} />
      <Text fontWeight='bold' style={styles.color(color)}>
        {text}
      </Text>
    </HFlow>
  )
}

const renderAtualizacao = (item: ListaRegulacamTableItem) => (
  <VFlow vSpacing={0}>
    {formatDate(item.ultimaEvolucao.dataEvolucao)}
    <Text>Há {humanizeDateTime(item.ultimaEvolucao.dataEvolucao)}</Text>
    {renderStatus(item.ultimaEvolucao.statusRegulacao)}
  </VFlow>
)

const renderUbs = (nome: string) => (
  <HFlow hSpacing={0.2} alignItems='center'>
    <Icon icon='hospital' size={1} />
    <Ellipsis maxLines={1}>{nome}</Ellipsis>
  </HFlow>
)

const renderSolicitacao = (item: ListaRegulacamTableItem) => {
  if (item.tipoRegulacao === TipoRegulacaoEnum.GUIA_ENCAMINHAMENTO) {
    return renderGuiaEncaminhamento(item)
  } else return renderExame(item)
}

const renderGuiaEncaminhamento = (item: ListaRegulacamTableItem) => {
  const guia = item.guiaEncaminhamento
  const { label, mainColor } = cuidadoCompartilhadoClassificacaoPrioridadeRecord[guia.classificacaoPrioridade]
  return (
    <VFlow vSpacing={0}>
      <Ellipsis maxLines={1} fontWeight='bold'>
        {guia.cbo.nome}
      </Ellipsis>
      <StatusSquare color={mainColor} description={label} />
      {guia.atendimentoProfissional.finalizadoEm && (
        <Ellipsis maxLines={1}>Solicitado há {humanizeDateTime(guia.atendimentoProfissional.finalizadoEm)}</Ellipsis>
      )}
    </VFlow>
  )
}

const renderExames = (procedimento: string, codigo: string, finalizadoEm?: Instant) => {
  return (
    <VFlow vSpacing={0}>
      <HFlow hSpacing={0.25}>
        <Text fontWeight='bold'>{procedimento}</Text>
        <Text> - {codigo}</Text>
      </HFlow>
      {!isUndefinedOrNull(finalizadoEm) && (
        <Ellipsis maxLines={1}>Solicitado há {humanizeDateTime(finalizadoEm)}</Ellipsis>
      )}
    </VFlow>
  )
}

const renderExame = (item: ListaRegulacamTableItem) => {
  const exame = item.exame.exame
  return renderExames(exame.descricao, exame.codigo, item.exame.atendimentoProfissional.finalizadoEm)
}

const renderCidadao = (item: ListaRegulacamTableItem) => {
  const cidadao = item.prontuario.cidadao
  const vinculacaoEquipe = cidadao.cidadaoVinculacaoEquipe

  return (
    <VFlow vSpacing={0}>
      <Ellipsis maxLines={1} fontWeight='bold'>
        {cidadao.nome}
      </Ellipsis>
      {vinculacaoEquipe?.equipe ? <Ellipsis maxLines={1}>{vinculacaoEquipe.equipe.nome}</Ellipsis> : 'Sem equipe'}
      {vinculacaoEquipe?.unidadeSaude && renderUbs(vinculacaoEquipe.unidadeSaude.nome)}
    </VFlow>
  )
}

const renderSolicitante = (item: ListaRegulacamTableItem) => {
  const lotacao = item.lotacao

  return (
    <VFlow vSpacing={0}>
      <Ellipsis maxLines={1} fontWeight='bold'>
        {lotacao.profissional.nome}
      </Ellipsis>
      <Ellipsis maxLines={1}>{lotacao.cbo.nome}</Ellipsis>
      {lotacao.equipe && <Ellipsis maxLines={1}>{lotacao.equipe.nome}</Ellipsis>}
      {renderUbs(lotacao.unidadeSaude.nome)}
    </VFlow>
  )
}

const styles = {
  tableFooter: css`
    border-top: 0;
  `,
  columnWidth: (size: string) =>
    css`
      padding: 0.5rem 1rem;
      width: ${size};
    `,
  color: (color: string) => css`
    color: ${color};
  `,
}
